import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import CloudIcon from '@mui/icons-material/Cloud';
import { toggleCDNError } from '../../api/client';

function CDNErrorCard({ pipeline, isActive, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      await toggleCDNError(pipeline.id);
      if (onToggle) {
        await onToggle();
      }
    } catch (error) {
      console.error('Failed to toggle CDN error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip
      title={
        isActive
          ? 'Click to restore original manifest URLs for CDN streams'
          : 'Click to simulate error on CDN streams only'
      }
    >
      <Card
        sx={{
          background: isActive
            ? isDark
              ? 'rgba(33, 150, 243, 0.2)'
              : 'rgba(33, 150, 243, 0.1)'
            : isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
          border: isActive
            ? `2px solid ${isDark ? '#64b5f6' : '#2196f3'}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isActive
              ? `0 4px 12px ${isDark ? 'rgba(33,150,243,0.3)' : 'rgba(33,150,243,0.3)'}`
              : `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <CardActionArea onClick={handleClick} disabled={loading}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.5,
              px: 1,
              minHeight: 80,
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#2196f3' }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <CloudIcon
                  sx={{
                    fontSize: 28,
                    color: isActive
                      ? isDark
                        ? '#64b5f6'
                        : '#2196f3'
                      : isDark
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.26)',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isActive
                      ? isDark
                        ? '#64b5f6'
                        : '#2196f3'
                      : 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  CDN Error
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: isDark ? '#90caf9' : '#64b5f6',
                    }}
                  >
                    ACTIVE
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
}

export default CDNErrorCard;
